import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { purgeMonetag } from '../monetag.config';

/**
 * AdContext — Schedules and triggers simulated popup ads for non-premium users.
 *
 * Policy (industry-standard, balanced for premium conversion):
 *  - Grace period: 90s after login before the first ad can fire
 *  - Cadence: random 90–180s between subsequent ads
 *  - Trigger points:
 *      • tab-switch (top-level nav)
 *      • global search action
 *      • PERSON OPEN (v5.7.41): every 5th entity-detail view fires an
 *        interstitial-style ad, regardless of time. The min-90s cadence is
 *        still enforced so spamming person cards in 10 seconds won't
 *        machine-gun the user with 5 ads — only the first crosses the
 *        cadence gate, the rest queue up via the counter reset.
 *  - Premium users: no ads ever
 *  - Guest users: ads still apply (incentive to register + upgrade)
 */

const FIRST_AD_DELAY_MS = 90 * 1000;        // 90s grace period
const MIN_INTERVAL_MS = 90 * 1000;          // 90s
const MAX_INTERVAL_MS = 180 * 1000;         // 180s
const PERSON_OPENS_PER_AD = 5;              // every 5th person card open

const AdContext = createContext({
  showAd: null,           // current ad object or null
  closeAd: () => {},
  suspendAd: () => {},
  triggerAdCheck: () => {},
  triggerAdOnPersonOpen: () => {},
  isPremium: false,
});

export const useAds = () => useContext(AdContext);

// Sample ad creatives (simulated — no real ad SDK)
const AD_CREATIVES = [
  {
    id: 'go-premium',
    title: 'Tired of these popups?',
    body: 'Go ad-free forever for a one-time $4.99. No gameplay advantage. Just zero interruptions.',
    cta: 'Remove Ads',
    ctaAction: 'premium',
    accent: 'hsl(var(--accent))',
  },
  {
    id: 'invite-friends',
    title: 'Play With Friends',
    body: 'Create a private league. Set custom rules. Talk smack in chat. It\'s more fun together.',
    cta: 'Create League',
    ctaAction: 'create-league',
    accent: 'hsl(173, 58%, 39%)',
  },
  {
    id: 'rulers-spotlight',
    title: 'Drafting Rulers This Week?',
    body: 'Election cycles are scoring big right now. Check the Explore tab to see today\'s hot Rulers.',
    cta: 'Browse Rulers',
    ctaAction: 'explore',
    accent: 'hsl(292, 38%, 22%)',
  },
];

function pickRandomAd() {
  // Bias 50% toward the "go premium" ad to maximize conversion intent
  if (Math.random() < 0.5) return AD_CREATIVES[0];
  return AD_CREATIVES[1 + Math.floor(Math.random() * (AD_CREATIVES.length - 1))];
}

function randomInterval() {
  return MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);
}

export function AdProvider({ children }) {
  const { user } = useAuth();
  const isPremium = !!user?.premium;
  const isAuthed = !!user;

  // Monetag REMOVED (2026-07) — it served adult/low-quality creatives. Purge
  // any legacy Monetag tag scripts + push service worker once on boot, for ALL
  // users (including those who still carry it from an earlier build). The web
  // now uses only the premium-aware in-app slots (Ezoic/AdSense/house).
  useEffect(() => { purgeMonetag(); }, []);

  const [showAd, setShowAd] = useState(null);
  const nextAdTimeRef = useRef(0);     // timestamp when next ad becomes eligible
  const initializedRef = useRef(false);
  // PERSON_OPEN trigger — count entity-detail opens; fire every Nth.
  // useRef (not useState) so increments don't cause re-renders of every
  // consumer of AdContext.
  const personOpenCountRef = useRef(0);
  // 2026-07 ad-skip exploit fix — clicking "Remove ads forever" (or a house
  // CTA) mid-countdown used to DISMISS the ad outright: users could escape
  // every timer by bouncing off the premium page. Now those clicks SUSPEND
  // the ad (remaining seconds stashed here) and it resumes at the next
  // trigger point unless the user actually went premium.
  const suspendedRef = useRef(null);

  // Reset schedule on user change (login / logout / upgrade)
  useEffect(() => {
    suspendedRef.current = null;
    if (isPremium || !isAuthed) {
      nextAdTimeRef.current = Number.POSITIVE_INFINITY;
      setShowAd(null);
      initializedRef.current = false;
      personOpenCountRef.current = 0;
      return;
    }
    nextAdTimeRef.current = Date.now() + FIRST_AD_DELAY_MS;
    initializedRef.current = true;
    personOpenCountRef.current = 0;
  }, [user?.id, isPremium, isAuthed]);

  const suspendAd = useCallback((secondsLeft) => {
    setShowAd((cur) => {
      if (cur) suspendedRef.current = { ad: cur, secondsLeft };
      return null;
    });
  }, []);

  const resumeSuspendedAd = useCallback(() => {
    if (!suspendedRef.current) return false;
    const { ad, secondsLeft } = suspendedRef.current;
    suspendedRef.current = null;
    setShowAd({ ...ad, resumeSeconds: secondsLeft });
    return true;
  }, []);

  const triggerAdCheck = useCallback(() => {
    if (!initializedRef.current || isPremium || !isAuthed) return;
    if (showAd) return;
    if (resumeSuspendedAd()) return;
    const now = Date.now();
    if (now < nextAdTimeRef.current) return;
    const ad = pickRandomAd();
    setShowAd(ad);
    // Schedule next eligibility immediately so closing doesn't trigger another instantly
    nextAdTimeRef.current = Date.now() + randomInterval();
  }, [isPremium, isAuthed, showAd, resumeSuspendedAd]);

  // v5.7.41 — fire an ad every PERSON_OPENS_PER_AD entity-detail views.
  // Same eligibility gates as the time-based trigger (grace period, no
  // double-firing, premium skip). When the count threshold is hit but
  // we're still inside the min-cadence window, we DON'T reset the count
  // — so the next eligible window fires immediately on the user's
  // next person open. This is what the user explicitly asked for:
  // "ad after 5 person-card opens" with anti-spam built in.
  const triggerAdOnPersonOpen = useCallback(() => {
    if (!initializedRef.current || isPremium || !isAuthed) return;
    if (showAd) return;
    if (resumeSuspendedAd()) return;
    personOpenCountRef.current += 1;
    if (personOpenCountRef.current < PERSON_OPENS_PER_AD) return;
    const now = Date.now();
    if (now < nextAdTimeRef.current) {
      // Hold the count at the threshold so the next opportunity past
      // grace fires; don't blast the user the moment grace ends though
      // — keep it bounded.
      personOpenCountRef.current = PERSON_OPENS_PER_AD;
      return;
    }
    personOpenCountRef.current = 0;
    const ad = pickRandomAd();
    setShowAd(ad);
    nextAdTimeRef.current = Date.now() + MIN_INTERVAL_MS;
  }, [isPremium, isAuthed, showAd, resumeSuspendedAd]);

  const closeAd = useCallback(() => {
    setShowAd(null);
  }, []);

  return (
    <AdContext.Provider value={{ showAd, closeAd, suspendAd, triggerAdCheck, triggerAdOnPersonOpen, isPremium }}>
      {children}
    </AdContext.Provider>
  );
}
